import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { TAcl } from '@global-types/app/app';
import {
	IconBolt,
	IconBuildingCommunity,
	IconBuildingFactory2,
	IconCoins,
	IconHome2,
	IconPerspective,
	IconRosette,
	IconSitemap,
	TablerIcon,
} from '@tabler/icons-react';

/**
 * Represents a single sidebar item.
 *
 * @typedef {Object} SidebarItem
 * @property {string} label - The translation key or label of the sidebar item.
 * @property {TablerIcon} icon - The icon component from Tabler Icons to display.
 * @property {string} [link] - The URL path to navigate to when clicked. Optional if the item has children.
 * @property {string[]} [allowedRoles] - Array of roles allowed to see this item. If omitted, all users can see it.
 * @property {SidebarItem[]} [links] - Nested child sidebar items for submenus.
 *
 * @example
 * const exampleItem: SidebarItem = {
 *   label: 'sidebar.overview',
 *   icon: IconHome2,
 *   link: '/',
 * };
 *
 * const exampleWithChildren: SidebarItem = {
 *   label: 'global.measurabilities',
 *   icon: IconPerspective,
 *   link: '/measurability-projects',
 *   allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN],
 *   links: [
 *     {
 *       label: 'sidebar.brands',
 *       icon: IconRosette,
 *       link: '/brands',
 *     },
 *   ],
 * };
 */
export type SidebarItem = {
	label: string;
	icon: TablerIcon;
	link?: string;
	allowedRoles?: string[];
	allowedAcls?: TAcl[];
	links?: SidebarItem[];
};

/**
 * List of all sidebar items for the application.
 *
 * This array contains the structure and access control for the sidebar menu.
 * Items with `allowedRoles` will only be visible to users with matching roles.
 * Nested `links` allow for submenus.
 *
 * @type {SidebarItem[]}
 *
 * @example
 * // Render only the top-level accessible items for a user:
 * const userAccessibleItems = sidebarItems.filter(item =>
 *   !item.allowedRoles || checkAccess({ allowedRoles: item.allowedRoles })
 * );
 */
export const sidebarItems: SidebarItem[] = [
	{
		label: 'sidebar.overview',
		icon: IconHome2,
		link: '/',
		allowedRoles: [
			ROLES.ADMIN,
			ROLES.SUPERADMIN,
			ROLES.STAFF,
			ROLES.ORGANIZATION_ADMIN,
			ROLES.ORGANIZATION_STAFF,
			ROLES.ORGANIZATION_MEMBER,
			ROLES.BRAND_ADMIN,
			ROLES.BRAND_STAFF,
			ROLES.PRODUCER_ADMIN,
			ROLES.PRODUCER_STAFF,
		],
	},
	{
		label: 'sidebar.offsets',
		icon: IconRosette,
		link: '/offsets',
		allowedAcls: [ACLS.OFFSETS.LIST],
	},
	{
		label: 'global.measurabilities',
		icon: IconPerspective,
		link: '/measurability-projects',
	},
	{
		label: 'global.additionalities',
		icon: IconCoins,
		link: '/additionality-projects',
	},
	{
		label: 'global.entityManagement',
		icon: IconSitemap,
		allowedRoles: [
			ROLES.ADMIN,
			ROLES.SUPERADMIN,
			ROLES.STAFF,
			ROLES.ORGANIZATION_ADMIN,
			ROLES.ORGANIZATION_STAFF,
		],
		links: [
			{
				label: 'global.brands',
				icon: IconBuildingCommunity,
				link: '/brands',
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.ORGANIZATION_STAFF,
				],
			},
			{
				label: 'global.producers',
				icon: IconBuildingFactory2,
				link: '/producers',
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.ORGANIZATION_STAFF,
				],
			},
		],
	},
	{
		label: 'global.supply',
		icon: IconBolt,
		allowedRoles: [
			ROLES.ADMIN,
			ROLES.SUPERADMIN,
			ROLES.STAFF,
			ROLES.ORGANIZATION_ADMIN,
			ROLES.PRODUCER_ADMIN,
			ROLES.PRODUCER_STAFF,
		],
		links: [
			{
				label: 'global.productions',
				icon: IconBuildingCommunity,
				link: '/productions',
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.PRODUCER_ADMIN,
					ROLES.PRODUCER_STAFF,
				],
			},
		],
	},
];
